import {
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild
} from '@angular/core';
import { PurchaseActionService, BidListService, BidInventoryItem, FinanceSource, ConfigurationState } from 'remarketing-angular-library';
import { OfferHistory } from '../../../shared/models/offer-history.model';
import { OfferModalDirective } from './offer-modal.directive';
import { OfferModalAction } from '../../../shared/directives/offer-modal-action/offer-modal-action';
import { AnalyticsService, OfferModalEvent } from 'smartauction-frontend-micro-library';
import { Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, finalize, mergeMapTo, take, takeUntil, tap } from 'rxjs/operators';
import { WithdrawOfferComponent } from './offer-actions/withdraw-offer/withdraw-offer.component';
import { CounterOfferComponent } from './offer-actions/counter-offer/counter-offer.component';
import { CancelOfferComponent } from './offer-actions/cancel-offer/cancel-offer.component';
import { get } from 'lodash';
import { FollowUpOfferComponent } from './offer-actions/follow-up-offer/follow-up-offer.component';
import { InitialOfferComponent } from './offer-actions/initial-offer/initial-offer.component';
import { OfferModalItem } from '../../../shared/models/offer-modal-item.model';
import { ModalComponent, Modal, SpinnerComponent } from 'remarketing-ui-components-lib';
import { BidItem } from 'remarketing-angular-library';
import { AuctionItemFee } from 'remarketing-angular-library/interfaces/auction-item';
import { OffersService } from '../../../shared/services/offers/offers.service';
import { ActivatedRoute } from '@angular/router';
import { OfferModalsService } from './service/offer-modals.service';
import { DripPricingService } from '../../../shared/services/drip-pricing/public-api';
import { Store } from '@ngxs/store';

import { TranslocoDirective } from '@jsverse/transloco';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'app-offer-modals',
    templateUrl: './offer-modals.component.html',
    styleUrls: ['./offer-modals.component.scss'],
    imports: [ModalComponent, SpinnerComponent, OfferModalDirective, TranslocoDirective]
})
export class OfferModalsComponent implements OnDestroy, Modal {
  @Input() tenantName: string;
  @Input() item: OfferModalItem;
  @Input() offerHistory: OfferHistory[];
  @Input() hasError = false;
  @Input() modalId = 'offer-modal';
  @Input() bidItem: BidInventoryItem;
  /**
   * The host page's fee payload, and the preferred fee source forwarded to the hosted offer action.
   *
   * Bound by the vehicle detail pages, which load it with the vehicle. Read once, when the modal opens:
   * the hosted offer action quotes the page's payload as of open, not a live feed of it. Left unset
   * by the bid list, which launches this host through ModalLauncherService and has no equivalent; see
   * where 'buyFee' is set for how the candidates resolve.
   */
  @Input() auctionItemFee: AuctionItemFee;
  @Input() financeSource: FinanceSource;
  @Output() closed = new EventEmitter<void>();
  @Output() modalEvent = new EventEmitter<OfferModalEvent>();
  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(OfferModalDirective) offerModalHost: OfferModalDirective;

  private closed$ = new Subject();
  public isLoading = true;
  activeModalRef: ComponentRef<OfferModalAction>;

  currentModalId: string;

  showError = false;
  errorMessage: string;

  get isViewOnly() {
    return this.store.selectSnapshot(ConfigurationState.hasRole('View_Only'));
  }

  /**
   * The flag's hook in CSS for this modal SHELL -- the 4px corners and the on-demand scrollbar.
   *
   * Gated on isDripPricingEnabled alone, deliberately, and not on the hosted action's own
   * showDripBreakdown. Two reasons:
   *
   *  1. This host cannot see that value. resolveActiveModal picks one of five components at runtime and
   *     the breakdown gate lives on whichever one it picked, behind that component's own fee-payload and
   *     amount checks. Reading it back out of activeModalRef would make the shell's styling depend on a
   *     child's internal state, re-evaluated per change-detection pass.
   *  2. Even if it could, it should not. Chrome is not content: whether a listing happens to carry a fee
   *     payload decides whether a BREAKDOWN renders, and tying the shell's corner radius to it would
   *     give one buyer square corners on one vehicle and rounded on the next.
   *
   * So the shell follows the flag and the content follows the data. Flag off, every offer modal renders
   * byte-for-byte today's chrome.
   *
   * BREADTH, stated rather than buried: this host wraps all five offer actions, so with the flag on the
   * radius and scrollbar change apply to Cancel Offer and Follow-Up Offer as well as the three the epic
   * has comps for. That is the intended reading of "only for drip pricing enabled users" -- a single
   * buyer meeting rounded and square corners in the same offer flow is the worse outcome -- but it is
   * the one call here that goes past the comps, so it is called out for sign-off.
   */
  get isAllInPricingEnabled(): boolean {
    return this.dripPricingService.isDripPricingEnabled;
  }

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private purchaseActionService: PurchaseActionService,
    private bidListService: BidListService,
    private readonly offersService: OffersService,
    private analyticsService: AnalyticsService,
    private readonly route: ActivatedRoute,
    private readonly _offerModalsService: OfferModalsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly dripPricingService: DripPricingService,
    private readonly store: Store
  ) {}

  show() {
    // comes from bidlist-inventory-item.component.ts OR item has been added to bid list / shared list
    if (this.bidItem) {
      this.item.buyerAuctionItemStatus = this.bidItem.buyerAuctionItemStatus;
      if (this.bidItem.hasOfferHistory) {
        this.getOfferHistory().pipe(take(1)).subscribe((offers: OfferHistory[]) => {
          this.offerHistory = offers;
          this.activateModalProperties();
        });
      } else {
        this.activateModalProperties();
      }
    } else {
      //Comes from auction-details.components.ts
      this.getOfferHistory().pipe(take(1)).subscribe((offers: OfferHistory[]) => {
        this.offerHistory = offers;
        this.activateModalProperties();
      });
    }
  }

  activateModalProperties() {
    this.setCurrentModalId();
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory<OfferModalAction>(
      this.resolveActiveModal()
    );
    const viewContainerRef = this.offerModalHost.viewContainerRef;
    viewContainerRef.clear();
    this.activeModalRef = viewContainerRef.createComponent(componentFactory);
    this.setModalProperties();
    this.modal.show();
    this.changeDetector.markForCheck();
    setTimeout(() => {
      this.changeDetector.markForCheck();
    }, 2000);
  }

  close() {
    this.closed$.next(true);
    this.modal.close();
    this.currentModalId = '';
    this.activeModalRef = undefined;
    this.offerHistory = undefined;
    this.isLoading = true;
  }

  private get offerStatus() {
    return get(this.offerHistory, '[0].offer.status');
  }

  getOfferHistory(): Observable<OfferHistory[]> {
    return this.offersService.getOfferHistory$(this.item.auctionItemUuid).pipe(takeUntil(this.closed$));
  }

  private setCurrentModalId() {
    const exclude = [
      'ACCEPTING_BIDS',
      'BIDDING_IN_PROGRESS',
      'LEADING',
      'OUTBID',
      'ON_HOLD',
      'VEHICLE_REMOVED',
      'VEHICLE_EXPIRED',
      'SOLD',
      'WON',
      'NOT_SOLD'
    ];
    if (exclude.includes(this.item.buyerAuctionItemStatus)) {
      this.currentModalId = 'Cancel-Offer';
      return;
    }
    switch (this.offerStatus) {
    case 'BUYER_COUNTERED':
      this.currentModalId = 'Withdraw-Offer-Buyer-Countered';
      break;
    case 'BUYER_OFFERED':
      this.currentModalId = 'Withdraw-Offer-Buyer-Offered';
      break;
    case 'SELLER_REVIVED':
      this.currentModalId = 'Counter-Offer-Seller-Revived';
      break;
    case 'SELLER_COUNTERED':
      this.currentModalId = 'Counter-Offer-Seller-Countered';
      break;
    case 'VEHICLE_REMOVED':
      this.currentModalId = 'Cancel-Offer-Vehicle-Removed';
      break;
    case 'ON_HOLD':
      this.currentModalId = 'Cancel-Offer-On-Hold';
      break;
    case 'SOLD':
      this.currentModalId = 'Cancel-Offer-Sold';
      break;
    case 'WON':
      this.currentModalId = 'Cancel-Offer-Won';
      break;
    case 'ACCEPTED':
      this.currentModalId = 'Cancel-Offer-Accepted';
      break;
    case 'CANCELLED':
      this.currentModalId = 'Cancelled';
      break;
    case 'OFFER_EXPIRED':
      this.currentModalId = 'Offer-Expired';
      break;
    case 'SELLER_DECLINED':
      this.currentModalId = 'Seller-Declined';
      break;
    case 'BUYER_DECLINED':
      this.currentModalId = 'Buyer-Declined';
      break;
    case 'BUYER_WITHDRAWN':
      this.currentModalId = 'Buyer-Withdrawn';
      break;
    case 'SELLER_OFFERED':
      this.currentModalId = 'Seller-Offered';
      break;
    case 'SELLER_WITHDRAWN': {
      switch (this.item.buyerPurchaseStatus.toLocaleLowerCase()) {
      case 'won': {
        this.currentModalId += '-Cancel-Offer';
        return;
      }
      default: {
        this.currentModalId += '-Follow-Up-Offer';
        return;
      }
      }
    }
    default: {
      this.currentModalId = 'Make-Offer';
      return;
    }
    }
  }

  private resolveActiveModal() {
    if (this.isViewOnly) {
      return InitialOfferComponent;
    }

    const exclude = [
      'ACCEPTING_BIDS',
      'BIDDING_IN_PROGRESS',
      'LEADING',
      'OUTBID',
      'ON_HOLD',
      'VEHICLE_REMOVED',
      'VEHICLE_EXPIRED',
      'SOLD',
      'WON',
      'NOT_SOLD'
    ];
    if (exclude.includes(this.item.buyerAuctionItemStatus)) {
      return CancelOfferComponent;
    }
    switch (this.offerStatus) {
    case 'BUYER_COUNTERED':
    case 'BUYER_OFFERED': {
      return WithdrawOfferComponent;
    }
    case 'SELLER_REVIVED':
    case 'SELLER_COUNTERED':
    case 'SELLER_OFFERED' :
    {
      return CounterOfferComponent;
    }
    case 'VEHICLE_REMOVED': {
      switch (this.item.buyerAuctionItemStatus.toLocaleLowerCase()) {
      case 'accepting_offers': {
        return FollowUpOfferComponent;
      }
      }
    }
    case 'ON_HOLD':
    case 'SOLD':
    case 'WON':
    case 'ACCEPTED': {
      return CancelOfferComponent;
    }
    case 'CANCELLED':
    case 'OFFER_EXPIRED':
    case 'SELLER_DECLINED':
    case 'BUYER_DECLINED':
    case 'BUYER_WITHDRAWN':
    case 'DECLINED_WITH_LP':
    case 'SELLER_WITHDRAWN': {
      switch (this.item.buyerPurchaseStatus.toLocaleLowerCase()) {
      case 'won': {
        return CancelOfferComponent;
      }
      default: {
        return FollowUpOfferComponent;
      }
      }
    }
    default: {
      return InitialOfferComponent;
    }
    }
  }

  private setModalProperties() {
    if (!this.activeModalRef || !this.activeModalRef.instance) {
      throw new Error('No modal set');
    }
    this.activeModalRef.instance.event
      .pipe(
        takeUntil(this.closed$),
        exhaustMap((event: OfferModalEvent) => {
          /*
            exhaustMap will ignore all new events until the current inner observable completes.
            For instance, a user presses "Make Offer" then immediately hits "Cancel". The "Cancel" will be ignored.
            This helps prevent an inconsistent state on the frontend/backend
           */
          const inventoryItemUuid = get(this.route, 'snapshot.parent.children[0].params.inventoryItemUuid', null);
          return of(event).pipe(
            tap(() => {
              this.setInstanceValue('waitingResponse', true);
              this.setInstanceValue('errorMessage', null);
            }),
            mergeMapTo(
              this._offerModalsService.handleOfferEvent(event, this.item).pipe(
                tap(offerResponse => {
                  switch(event.type) {
                    case 'MakeOffer':
                      event.bidItem = (offerResponse).bidlistItemDto;
                      this.analyticsService.fireBuyActionEvent('makeBuyAction', 1, [this.item.auctionItemUuid]);
                      break;
                    case 'AcceptOffer':
                      this.analyticsService.fireBuyActionEvent('acceptBuyAction', 1, [this.item.auctionItemUuid]);
                      break;
                    case 'CounterOffer':
                      this.analyticsService.fireBuyActionEvent('counterBuyAction', 1, [this.item.auctionItemUuid]);
                      break;
                    case 'DeclineOffer':
                      this.analyticsService.fireBuyActionEvent('declineBuyAction', 1, [this.item.auctionItemUuid]);
                      break;
                    case 'WithdrawOffer':
                      this.analyticsService.fireBuyActionEvent('withdrawBuyAction', 1, [this.item.auctionItemUuid]);
                      break;
                  }
                  this.modalEvent.emit(event);
                }),
              )
            ),
            catchError(error =>
              this._offerModalsService.handleOfferError(error).pipe(
                tap(message => {
                  this.errorMessage = error.error.errors[0].code;
                  this.activeModalRef.instance.setError(this.errorMessage);
                }),
                tap(message => {
                  // send tracking data
                  this.analyticsService.track('customError', message, null, this.analyticsService.createOfferModalEventAnalyticsPayload(event, message, inventoryItemUuid));
                })
              )
            ),
            tap(response => {
              /** response is string, which means the request fails, return error message */
              if (typeof response === 'string') {
                this.hasError = true;
              } else {
                this.hasError = false;
              }
            }),
            finalize(() => {
              this.setInstanceValue('waitingResponse', false);

              if (this.hasError) {
                this.setInstanceValue('successfulResponse', false);
              } else {
                this.setInstanceValue('successfulResponse', true);
              }
              this.changeDetector.markForCheck();
            })
          );
        })
      )
      .subscribe();

    this.setInstanceValue('modalId', this.currentModalId);
    this.setInstanceValue('item', this.item);
    this.setInstanceValue('offerHistory', this.offerHistory);
    this.setInstanceValue('tenantName', this.tenantName);
    this.setInstanceValue('bidItem', this.bidItem);
    this.setInstanceValue('financeSource', this.financeSource);

    if (this.item) {
      this.bidListService
        .getBidListItem(this.item.auctionItemUuid)
        .pipe(catchError(() => of(undefined)))
        .subscribe((bidListItem: BidItem) => {
          // Was mapped to buyerConfirmationRequired in the pipe; the whole response is kept now so its
          // buyFee can be forwarded below. !! and ?. preserve the previous outcome exactly: the old
          // map() threw on an absent response and the catchError turned that into false.
          this.setInstanceValue('showWarning', !!bidListItem?.buyerConfirmationRequired);

          // The fee source for the all-in breakdown (M4.4), forwarded to whichever offer action is
          // hosted. Three candidates in falling order of authority.
          //
          // auctionItemFee is the detail page's own payload: present for any vehicle the buyer can
          // view, bid list or not. It is preferred even when bidItem exists, so the offer modal opens
          // on the same fees the page behind it is pricing with. It is read here, once: the modal
          // quotes the page's payload as of open, and is not updated if the page's fees change while
          // it is showing.
          //
          // bidItem is what this host is given for a vehicle already on the buyer's list. It stays
          // ahead of the fetch below so the bid list launch path -- which binds no auctionItemFee --
          // is unchanged.
          //
          // bidListItem is this response, which was already being fetched and thrown away. It remains
          // the last resort for any host that supplies neither of the above.
          //
          // An earlier revision read `bidItem?.buyFee ?? bidListItem?.buyFee` and assumed the fetch
          // covered every vehicle not on the list. It does not: off-list there is no bid list item to
          // take a buyFee from, so Make Offer from a Buy Search detail page fell back to today's prose
          // while the page around it showed all-in prices. Bid list membership is not a drip gate.
          //
          // A failed fetch with no auctionItemFee leaves this undefined, which switches the breakdown
          // off rather than quoting a total with the fees missing.
          this.setInstanceValue(
            'buyFee',
            this.auctionItemFee?.buyFee ?? this.bidItem?.buyFee ?? bidListItem?.buyFee
          );

          this.isLoading = false;
          this.modal.focusTitle();
          this.changeDetector.markForCheck();
        });
    } else {
      this.isLoading = false;
      setTimeout(() => {
        this.changeDetector.markForCheck();
        this.modal.focusTitle();
      }, 50);
    }
  }

  private setInstanceValue<T extends keyof OfferModalAction, K extends OfferModalAction[T]>(key: T, value: K) {
    this.activeModalRef.instance[key] = value;
  }

  ngOnDestroy() {
    this.closed$.next(true);
    this.closed$.complete();
  }
}
